/**
 * useAuthForm Hook
 * Custom hook for authentication forms using react-hook-form and Zod validation
 * Provides consistent form handling, validation, and error management
 */

import { zodResolver } from "@hookform/resolvers/zod"
import type { ApiError } from "@shared-types/api.types"
import type { AxiosError } from "axios"
import { useState } from "react"
import { type DefaultValues, useForm } from "react-hook-form"
import type { z } from "zod"
import { getFieldErrors, transformApiError } from "@/lib/errorTransformer"

/**
 * Custom hook for authentication forms
 *
 * @example
 * ```tsx
 * const { form, handleSubmit, isSubmitting, serverError } = useAuthForm(
 *   loginSchema,
 *   async (data) => {
 *     await login(data.email, data.password)
 *     navigate('/dashboard')
 *   }
 * )
 * ```
 */
export function useAuthForm<T extends Record<string, unknown>>(
  // biome-ignore lint/suspicious/noExplicitAny: Required for zodResolver compatibility
  schema: z.ZodType<T, any, any>,
  onSubmit: (data: T) => Promise<void>,
  options?: {
    skipValidationFields?: string[]
  },
) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string> | null>(null)

  const form = useForm({
    resolver: zodResolver(schema),
    mode: "onSubmit", // Validate only on form submit
    defaultValues: {
      countryCode: "",
      phoneNumber: "",
      streetAddress: "",
      city: "",
      state: "",
      zipcode: "",
      country: "",
    } as unknown as DefaultValues<T>,
  })

  const handleSubmit = async (e?: React.BaseSyntheticEvent) => {
    if (e) {
      e.preventDefault()
    }

    setIsSubmitting(true)
    setServerError(null)
    setFieldErrors(null)

    try {
      // Small delay to ensure form values are properly synchronized
      // Addresses timing issue when submitting via Enter key
      await new Promise(resolve => setTimeout(resolve, 10))
      // Validate with react-hook-form (optionally skip certain fields)
      const fieldsToValidate = options?.skipValidationFields
        ? Object.keys(form.getValues()).filter(
            field => !options.skipValidationFields?.includes(field),
          )
        : undefined
      const isValid = fieldsToValidate ? await form.trigger(fieldsToValidate) : await form.trigger()
      if (!isValid) {
        setIsSubmitting(false)
        return
      }

      // Get validated values
      const values = form.getValues()

      // Call submit handler
      await onSubmit(values as T)
    } catch (error) {
      // Extract field-level errors
      const err = error as AxiosError<ApiError>
      const errors = getFieldErrors(err)
      if (errors) {
        setFieldErrors(errors)
        // Also set individual form errors
        for (const [field, message] of Object.entries(errors)) {
          form.setError(field as string, { type: "server", message })
        }
      }
      // Transform error to user-friendly message
      const errorMessage = transformApiError(err)
      setServerError(errorMessage)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    form,
    handleSubmit,
    isSubmitting,
    serverError,
    fieldErrors,
    clearServerError: () => setServerError(null),
    clearFieldErrors: () => setFieldErrors(null),
  }
}
